import { Mood as MoodIcon, Settings as SettingsIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import type { ApiConfig, Persona, PersonaCreateSchema, PersonaUpdateSchema } from "../types.ts";

interface PersonaDialogProps {
  open: boolean;
  onClose: () => void;
  persona?: Persona;
  appConfigs: ApiConfig["configs"];
  onSubmit: (data: PersonaCreateSchema | PersonaUpdateSchema) => void;
  isLoading: boolean;
  isEdit?: boolean;
}

export default function PersonaDialog({
  open,
  onClose,
  persona,
  appConfigs,
  onSubmit,
  isLoading,
  isEdit = false,
}: PersonaDialogProps) {
  const [formData, setFormData] = useState<PersonaCreateSchema>({
    app_config_id: persona?.app_config_id || "",
    name: persona?.name || "",
    description: persona?.description || "",
    system_prompt: persona?.system_prompt || "",
    user: persona?.user || "",
    password: persona?.password || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (persona && isEdit) {
      setFormData({
        app_config_id: persona.app_config_id,
        name: persona.name,
        description: persona.description || "",
        system_prompt: persona.system_prompt,
        user: persona.user || "",
        password: persona.password || "",
      });
    } else if (!isEdit) {
      setFormData({
        app_config_id: "",
        name: "",
        description: "",
        system_prompt: "",
        user: "",
        password: "",
      });
    }
  }, [persona, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.system_prompt.trim()) {
      newErrors.system_prompt = "System prompt is required";
    } else if (formData.system_prompt.length > 10000) {
      newErrors.system_prompt = "System prompt too long (max 10,000 characters)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      formData.app_config_id =
        formData.app_config_id === "all" ? undefined : formData.app_config_id;
      if (isEdit) {
        const updateData: PersonaUpdateSchema = {
          app_config_id: formData.app_config_id,
          name: formData.name,
          description: formData.description,
          system_prompt: formData.system_prompt,
          user: formData.user,
          password: formData.password,
        };
        onSubmit(updateData);
      } else {
        onSubmit(formData);
      }
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
        },
      }}
      TransitionProps={{
        timeout: 200,
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            p: 0,
            backgroundColor: "background.default",
            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          }}
        >
          <Box sx={{ p: 3, pb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                }}
              >
                <MoodIcon sx={{ fontSize: 36 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    color: "text.primary",
                    fontSize: "1.125rem",
                    lineHeight: 1.4,
                  }}
                >
                  {isEdit ? "Edit Persona" : "Create New Persona"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    mt: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  {isEdit
                    ? "Update your AI persona's configuration and behavior"
                    : "Define a new AI persona for your application tests"}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, maxHeight: "70vh", overflow: "auto" }}>
          <Box sx={{ p: 3 }}>
            <Stack spacing={4}>
              {/* Basic Information Section */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: "0.875rem",
                    mb: 2,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Basic Information
                </Typography>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Persona Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    error={!!errors.name}
                    helperText={errors.name}
                    placeholder="e.g., Customer Support Agent"
                    size="medium"
                    sx={{
                      "& .MuiInputLabel-root": {
                        color: "text.secondary",
                        fontSize: "0.875rem",
                        "&.Mui-focused": {
                          color: "primary.main",
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        backgroundColor: "common.white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "background.default",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "common.white",
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.12)",
                        borderWidth: 1,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(25, 118, 210, 0.3)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        borderWidth: 1.5,
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={2}
                    placeholder="Brief description of the persona's role and characteristics"
                    size="medium"
                    sx={{
                      "& .MuiInputLabel-root": {
                        color: "text.secondary",
                        fontSize: "0.875rem",
                        "&.Mui-focused": {
                          color: "primary.main",
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        backgroundColor: "common.white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "background.default",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "common.white",
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.12)",
                        borderWidth: 1,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(25, 118, 210, 0.3)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        borderWidth: 1.5,
                      },
                    }}
                  />
                  <FormControl fullWidth size="medium">
                    <InputLabel
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.875rem",
                        "&.Mui-focused": {
                          color: "primary.main",
                        },
                      }}
                    >
                      App Configuration (Optional)
                    </InputLabel>
                    <Select
                      value={formData.app_config_id || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          app_config_id: e.target.value || undefined,
                        })
                      }
                      label="App Configuration (Optional)"
                      sx={{
                        borderRadius: 1,
                        backgroundColor: "common.white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "background.default",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "common.white",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.12)",
                          borderWidth: 1,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(25, 118, 210, 0.3)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                          borderWidth: 1.5,
                        },
                      }}
                    >
                      <MenuItem key="" value="all">
                        <em>all</em>
                      </MenuItem>
                      {appConfigs.map((config) => (
                        <MenuItem key={config.id} value={config.id}>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.75rem",
                              }}
                            >
                              {config.base_url}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>

              {/* Configuration Section */}
              <Box>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.875rem",
                        mb: 1.5,
                        fontWeight: 500,
                      }}
                    >
                      Credentials (Optional)
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Username"
                        value={formData.user}
                        onChange={(e) => setFormData({ ...formData, user: e.target.value })}
                        placeholder="Username for the persona"
                        size="medium"
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "text.secondary",
                            fontSize: "0.875rem",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                            backgroundColor: "common.white",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "background.default",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "common.white",
                            },
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            borderWidth: 1,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(25, 118, 210, 0.3)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "primary.main",
                            borderWidth: 1.5,
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Password for the persona"
                        size="medium"
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "text.secondary",
                            fontSize: "0.875rem",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                            backgroundColor: "common.white",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "background.default",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "common.white",
                            },
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            borderWidth: 1,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(25, 118, 210, 0.3)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "primary.main",
                            borderWidth: 1.5,
                          },
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              {/* System Prompt Section */}
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <SettingsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Behavior & Instructions
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  label="System Prompt"
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                  multiline
                  rows={6}
                  error={!!errors.system_prompt}
                  helperText={
                    errors.system_prompt || `${formData.system_prompt.length}/10,000 characters`
                  }
                  placeholder="Define the persona's behavior, tone, expertise, and how they should interact with users..."
                  size="medium"
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "text.secondary",
                      fontSize: "0.875rem",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      backgroundColor: "common.white",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "background.default",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "common.white",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      borderWidth: 1,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(25, 118, 210, 0.3)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                      borderWidth: 1.5,
                    },
                    "& .MuiInputBase-input": {
                      fontFamily:
                        'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                    },
                    "& .MuiFormHelperText-root": {
                      fontSize: "0.75rem",
                      color: errors.system_prompt ? "error.main" : "text.secondary",
                      marginLeft: 0,
                    },
                  }}
                />
              </Box>
            </Stack>
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.06)" }} />

        <DialogActions
          sx={{
            p: 3,
            backgroundColor: "background.default",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
              },
              "&:disabled": {
                backgroundColor: "grey.200",
                color: "text.disabled",
                boxShadow: "none",
              },
            }}
          >
            {isEdit ? "Update Persona" : "Create Persona"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
